// src/components/layout/Sidebar.tsx
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  LayoutDashboard,
  School,
  Users,
  BookOpen,
  GraduationCap,
  UserCheck,
  FileText,
  Settings,
  BarChart2,
  MessageSquare,
  MessageCircle,
  Gamepad,
  Zap,
  Edit3,
  Cpu,
  FileCode2,
  Image,
  Pen,
  ClipboardList,
  CreditCard,
  Tag,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { clsx } from "clsx";
import { ROUTES, USER_ROLES } from "../../utils/constants";
import { useTranslation } from "react-i18next";
import { useFeatureAccess } from "../../contexts/FeatureAccessContext";
import type { SchoolStudentFeatureKey } from "../../modules/platform/types/featureAccess";
import { useStudentMe } from "../../modules/roster/hooks/useStudentMe";
import { isGrade2 } from "../../utils/studentGrade";

// ✅ COMMENTED: i18n disabled (English only for now)
// import { useTranslation } from "react-i18next";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItemChild {
  labelKey: string;
  route: string;
  icon: React.ElementType;
}

interface NavItem {
  labelKey: string;
  icon: React.ElementType;
  route: string;
  roles: string[];
  /** When set, isActive is true if pathname matches any of these routes */
  activeRoutes?: string[];
  /** Teacher `/teacher/assignments`: highlight when `?open=communication` (communication hub). */
  teacherAssignmentsCommTasks?: boolean;
  /** Teacher `/teacher/assignments`: highlight when not on comm tasks query. */
  teacherAssignmentsDefault?: boolean;
  /** When set, item is an accordion parent with submenu */
  children?: NavItemChild[];
  /** Gated by GET /platform/feature-access/me for students; omit for always-visible items */
  schoolFeatureKey?: SchoolStudentFeatureKey;
}

// ✅ English-only sidebar labels (no t())
const LABELS: Record<string, string> = {
  // DEV TEAM
  "sidebar.dev.dashboard": "Dashboard",
  "sidebar.dev.schools": "Schools",
  "sidebar.dev.auditLogs": "Audit Logs",
  "sidebar.dev.plans": "Plans",
  "sidebar.dev.coupons": "Coupons",
  "sidebar.dev.emailConfigs": "Email Configs",
  "sidebar.dev.emailTemplates": "Email Templates",
  "sidebar.dev.promptTemplates": "Prompt library",
  "sidebar.dev.inviteDev": "Invite Dev",

  // SCHOOL ADMIN
  "sidebar.admin.dashboard": "Dashboard",
  "sidebar.admin.schoolProfile": "School Profile",
  "sidebar.admin.grades": "Classes",
  "sidebar.admin.sections": "Sections",
  "sidebar.admin.students": "Students",
  "sidebar.admin.teachers": "Teachers",
  "sidebar.admin.parents": "Parents",
  "sidebar.admin.billing": "Billing",
  "sidebar.admin.emailConfigs": "Email Configs",
  "sidebar.admin.emailTemplates": "Email Templates",

  // TEACHER
  "sidebar.teacher.dashboard": "Dashboard",
  "sidebar.teacher.mySections": "My Sections",
  "sidebar.teacher.assignments": "Assignments",
  "sidebar.teacher.analytics": "Analytics",
  "sidebar.teacher.tutorAnalytics": "Tutor Analytics",
  "sidebar.teacher.canDo": "Can Do",
  "sidebar.teacher.commProgress": "Can Do progress",
  // "sidebar.teacher.commAssignments": "Communication assignments",
  // "sidebar.teacher.commAnalytics": "Communication analytics",
  // "sidebar.teacher.commAlerts": "Communication alerts",
  "sidebar.teacher.scenarioCanDo": "Scenario Can-Do",
  "sidebar.teacher.profile": "Profile",
  "sidebar.teacher.settings": "Settings",

  // STUDENT
  "sidebar.student.home": "Home",
  "sidebar.student.chat": "Conversation",
  "sidebar.student.tamilTutor": "Tamil Tutor",
  "sidebar.student.games": "Games",
  "sidebar.student.phonicsLab": "Phonics Lab",
  // "sidebar.student.courses": "Courses",
  "sidebar.student.assignments": "Assignments",
  "sidebar.student.activity": "Activity",
  "sidebar.student.storyStudio": "Story Studio",
  "sidebar.student.aiTutor": "AI Tutor",
  "sidebar.student.communicationTutor": "Communication Tutor",
  "sidebar.student.commAssignments": "Communication practice tasks",
  "sidebar.student.scenarioConversation": "Scenario Conversation",
  "sidebar.student.pictureTalk": "Picture Talk",
  "sidebar.student.writing": "Writing",
  "sidebar.student.library": "Library",
  "sidebar.student.progress": "Progress",
  "sidebar.student.analytics": "Analytics",
  "sidebar.student.settings": "Settings",
  "sidebar.student.profile": "Profile",

  // PARENT
  "sidebar.parent.dashboard": "Dashboard",
  "sidebar.parent.children": "Children",
  "sidebar.parent.linkChild": "Link Child",
  "sidebar.parent.progress": "Learning progress",
};

const PEOPLE_ROUTES = [ROUTES.ADMIN_STUDENTS, ROUTES.ADMIN_TEACHERS, ROUTES.ADMIN_PARENTS] as const;
const isPeopleRoute = (pathname: string) =>
  PEOPLE_ROUTES.some((r) => pathname === r || pathname.startsWith(r + "/"));

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { loading: featureAccessLoading, isSchoolFeatureEnabled } = useFeatureAccess();
  const { grade: rosterGrade, isRosterLoading } = useStudentMe();
  const location = useLocation();
  const { t } = useTranslation();
  const [peopleOpen, setPeopleOpen] = useState(() => isPeopleRoute(location.pathname));

  useEffect(() => {
    if (isPeopleRoute(location.pathname)) setPeopleOpen(true);
  }, [location.pathname]);

  if (!user) return null;

  const hideStudentActivityNav =
    user.roles.includes(USER_ROLES.STUDENT) && (isRosterLoading || isGrade2(rosterGrade));

  const navItems: NavItem[] = [];

  // DEV TEAM
  if (user.roles.includes(USER_ROLES.DEV_TEAM)) {
    navItems.push(
      { labelKey: "sidebar.dev.dashboard", icon: LayoutDashboard, route: ROUTES.DEV_DASHBOARD, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.schools", icon: School, route: ROUTES.DEV_SCHOOLS, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.auditLogs", icon: FileText, route: ROUTES.DEV_AUDIT_LOGS, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.plans", icon: CreditCard, route: ROUTES.DEV_PLANS, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.coupons", icon: Tag, route: ROUTES.PROFILE_COUPONS, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.emailConfigs", icon: Settings, route: ROUTES.DEV_COMMS_EMAIL_CONFIGS, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.emailTemplates", icon: FileText, route: ROUTES.DEV_COMMS_EMAIL_TEMPLATES, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.promptTemplates", icon: FileCode2, route: ROUTES.DEV_PROMPT_TEMPLATES, roles: [USER_ROLES.DEV_TEAM] },
      { labelKey: "sidebar.dev.inviteDev", icon: UserCheck, route: ROUTES.DEV_INVITE, roles: [USER_ROLES.DEV_TEAM] }
    );
  }

  // SCHOOL ADMIN (People = Students + Teachers + Parents under one item; sub-sidebar shows the three)
  if (user.roles.includes(USER_ROLES.SCHOOL_ADMIN)) {
    navItems.push(
      { labelKey: "sidebar.admin.dashboard", icon: LayoutDashboard, route: ROUTES.ADMIN_DASHBOARD, roles: [USER_ROLES.SCHOOL_ADMIN] },
      { labelKey: "sidebar.admin.schoolProfile", icon: School, route: ROUTES.ADMIN_SCHOOL, roles: [USER_ROLES.SCHOOL_ADMIN] },
      { labelKey: "sidebar.admin.grades", icon: BookOpen, route: ROUTES.ADMIN_CLASSES, roles: [USER_ROLES.SCHOOL_ADMIN] },
      { labelKey: "sidebar.admin.sections", icon: Users, route: ROUTES.ADMIN_SECTIONS, roles: [USER_ROLES.SCHOOL_ADMIN] },
      {
        labelKey: "sidebar.admin.people",
        icon: Users,
        route: ROUTES.ADMIN_STUDENTS,
        roles: [USER_ROLES.SCHOOL_ADMIN],
        activeRoutes: [ROUTES.ADMIN_STUDENTS, ROUTES.ADMIN_TEACHERS, ROUTES.ADMIN_PARENTS],
        children: [
          { labelKey: "sidebar.admin.teachers", route: ROUTES.ADMIN_TEACHERS, icon: Users },
          { labelKey: "sidebar.admin.students", route: ROUTES.ADMIN_STUDENTS, icon: GraduationCap },
          { labelKey: "sidebar.admin.parents", route: ROUTES.ADMIN_PARENTS, icon: UserCheck },
        ],
      },
      { labelKey: "sidebar.admin.billing", icon: CreditCard, route: ROUTES.ADMIN_BILLING, roles: [USER_ROLES.SCHOOL_ADMIN] },
      { labelKey: "sidebar.admin.emailConfigs", icon: Settings, route: ROUTES.ADMIN_COMMS_EMAIL_CONFIGS, roles: [USER_ROLES.SCHOOL_ADMIN] },
      { labelKey: "sidebar.admin.emailTemplates", icon: FileText, route: ROUTES.ADMIN_COMMS_EMAIL_TEMPLATES, roles: [USER_ROLES.SCHOOL_ADMIN] }
    );
  }

  // TEACHER
  if (user.roles.includes(USER_ROLES.TEACHER)) {
    navItems.push(
      { labelKey: "sidebar.teacher.dashboard", icon: LayoutDashboard, route: ROUTES.TEACHER_DASHBOARD, roles: [USER_ROLES.TEACHER] },
      { labelKey: "sidebar.teacher.mySections", icon: BookOpen, route: ROUTES.TEACHER_MY_SECTIONS, roles: [USER_ROLES.TEACHER] },
      {
        labelKey: "sidebar.teacher.assignments",
        icon: FileText,
        route: ROUTES.TEACHER_ASSIGNMENTS,
        roles: [USER_ROLES.TEACHER],
        teacherAssignmentsDefault: true,
      },
      // { labelKey: "sidebar.teacher.analytics", icon: BarChart2, route: ROUTES.TEACHER_ANALYTICS, roles: [USER_ROLES.TEACHER] },
      { labelKey: "sidebar.teacher.tutorAnalytics", icon: BarChart2, route: ROUTES.TEACHER_TUTOR_ANALYTICS, roles: [USER_ROLES.TEACHER] },
      { labelKey: "sidebar.teacher.canDo", icon: Tag, route: ROUTES.TEACHER_CAN_DO, roles: [USER_ROLES.TEACHER] },
      { labelKey: "sidebar.teacher.commProgress", icon: MessageCircle, route: ROUTES.TEACHER_STUDENT_COMMUNICATION_PROGRESS, roles: [USER_ROLES.TEACHER] },
      // {
      //   labelKey: "sidebar.teacher.commAssignments",
      //   icon: ClipboardCheck,
      //   route: ROUTES.TEACHER_ASSIGNMENTS_COMM_TASKS,
      //   roles: [USER_ROLES.TEACHER],
      //   teacherAssignmentsCommTasks: true,
      // },
      // { labelKey: "sidebar.teacher.commAnalytics", icon: LineChart, route: ROUTES.TEACHER_COMMUNICATION_ANALYTICS, roles: [USER_ROLES.TEACHER] },
      // { labelKey: "sidebar.teacher.commAlerts", icon: Bell, route: ROUTES.TEACHER_COMMUNICATION_ALERTS, roles: [USER_ROLES.TEACHER] },
      { labelKey: "sidebar.teacher.scenarioCanDo", icon: MessageSquare, route: ROUTES.TEACHER_SCENARIO_CAN_DO, roles: [USER_ROLES.TEACHER] },
      { labelKey: "sidebar.teacher.profile", icon: UserCheck, route: ROUTES.TEACHER_PROFILE, roles: [USER_ROLES.TEACHER] },
      { labelKey: "sidebar.teacher.settings", icon: Settings, route: ROUTES.TEACHER_SETTINGS, roles: [USER_ROLES.TEACHER] }
    );
  }

  // STUDENT
  if (user.roles.includes(USER_ROLES.STUDENT)) {
    navItems.push(
      { labelKey: "sidebar.student.home", icon: LayoutDashboard, route: ROUTES.STUDENT_DASHBOARD, roles: [USER_ROLES.STUDENT] },
      // { labelKey: "sidebar.student.chat", icon: MessageSquare, route: ROUTES.STUDENT_CONVERSATION, roles: [USER_ROLES.STUDENT] },
      {
        labelKey: "sidebar.student.tamilTutor",
        icon: BookOpen,
        route: ROUTES.STUDENT_TAMIL_TUTOR,
        roles: [USER_ROLES.STUDENT],
        schoolFeatureKey: "ai_tutor",
      },
      // { labelKey: "sidebar.student.aiTutor", icon: Cpu, route: ROUTES.STUDENT_TAMIL_TUTOR, roles: [USER_ROLES.STUDENT] },
      {
        labelKey: "sidebar.student.communicationTutor",
        icon: MessageCircle,
        route: ROUTES.STUDENT_COMMUNICATION_TUTOR,
        roles: [USER_ROLES.STUDENT],
        schoolFeatureKey: "communication_tutor",
      },
      {
        labelKey: "sidebar.student.scenarioConversation",
        icon: MessageCircle,
        route: ROUTES.STUDENT_SCENARIO_CONVERSATION,
        roles: [USER_ROLES.STUDENT],
        schoolFeatureKey: "scenario_based_convo",
      },
      {
        labelKey: "sidebar.student.pictureTalk",
        icon: Image,
        route: ROUTES.STUDENT_PICTURE_TALK,
        roles: [USER_ROLES.STUDENT],
        schoolFeatureKey: "picture_talk",
        activeRoutes: [ROUTES.STUDENT_PICTURE_TALK],
      },
      // { labelKey: "sidebar.student.games", icon: Gamepad, route: ROUTES.STUDENT_GAMES, roles: [USER_ROLES.STUDENT] },
      { labelKey: "sidebar.student.phonicsLab", icon: Zap, route: ROUTES.STUDENT_PHONICS_LAB, roles: [USER_ROLES.STUDENT] },
      {
        labelKey: "sidebar.student.storyStudio",
        icon: Edit3,
        route: ROUTES.STUDENT_STORY_STUDIO,
        roles: [USER_ROLES.STUDENT],
        schoolFeatureKey: "story_studio",
      },
      {
        labelKey: "sidebar.student.writing",
        icon: Pen,
        route: ROUTES.STUDENT_WRITING,
        roles: [USER_ROLES.STUDENT],
        schoolFeatureKey: "grammar",
      },
      { labelKey: "sidebar.student.assignments", icon: FileText, route: ROUTES.STUDENT_ASSIGNMENTS, roles: [USER_ROLES.STUDENT] },
      { labelKey: "sidebar.student.activity", icon: ClipboardList, route: ROUTES.STUDENT_ACTIVITY, roles: [USER_ROLES.STUDENT] },

      // { labelKey: "sidebar.student.courses", icon: BookOpen, route: ROUTES.STUDENT_COURSES, roles: [USER_ROLES.STUDENT], activeRoutes: [ROUTES.STUDENT_COURSES, '/student/grade'] },
      { labelKey: "sidebar.student.analytics", icon: BarChart2, route: ROUTES.STUDENT_ANALYTICS, roles: [USER_ROLES.STUDENT] },
      // { labelKey: "sidebar.student.aiTutor", icon: Cpu, route: ROUTES.STUDENT_AI_TUTOR_AVATAR, roles: [USER_ROLES.STUDENT] },
      // { labelKey: "sidebar.student.pictureTalk", icon: Image, route: ROUTES.STUDENT_PICTURE_TALK, roles: [USER_ROLES.STUDENT] },
      // { labelKey: "sidebar.student.library", icon: BookOpen, route: ROUTES.STUDENT_LIBRARY, roles: [USER_ROLES.STUDENT] },
      // { labelKey: "sidebar.student.progress", icon: BarChart2, route: ROUTES.STUDENT_PROGRESS, roles: [USER_ROLES.STUDENT] },
      // { labelKey: "sidebar.student.settings", icon: Settings, route: ROUTES.STUDENT_SETTINGS, roles: [USER_ROLES.STUDENT] },
      { labelKey: "sidebar.student.profile", icon: UserCheck, route: ROUTES.STUDENT_PROFILE, roles: [USER_ROLES.STUDENT] }
    );
  }

  // PARENT
  if (user.roles.includes(USER_ROLES.PARENT)) {
    navItems.push(
      { labelKey: "sidebar.parent.dashboard", icon: LayoutDashboard, route: ROUTES.PARENT_DASHBOARD, roles: [USER_ROLES.PARENT] },
      { labelKey: "sidebar.parent.children", icon: Users, route: ROUTES.PARENT_CHILDREN, roles: [USER_ROLES.PARENT] },
      { labelKey: "sidebar.parent.linkChild", icon: UserCheck, route: ROUTES.PARENT_LINK_CHILD, roles: [USER_ROLES.PARENT] },
      { labelKey: "sidebar.parent.progress", icon: BarChart2, route: ROUTES.PARENT_COMMUNICATION_PROGRESS, roles: [USER_ROLES.PARENT] }
    );
  }

  const filteredNavItems = navItems.filter((item) => {
    if (!item.roles.some((role) => user.roles.includes(role as any))) return false;
    if (item.route === ROUTES.STUDENT_ACTIVITY && hideStudentActivityNav) return false;
    if (item.schoolFeatureKey) {
      if (featureAccessLoading) return false;
      if (!isSchoolFeatureEnabled(item.schoolFeatureKey)) return false;
    }
    return true;
  });

  return (
    <div className="flex flex-col h-full min-h-0" aria-hidden={!isOpen}>
      <div className="flex items-center justify-between p-4 border-b border-app-border dark:border-gray-700 lg:hidden" />

      <nav className="flex-1 min-h-0 overflow-y-auto p-4 space-y-1 bg-app-surface dark:bg-slate-950">
        {filteredNavItems.map((item) => {
          const Icon = item.icon;
          const isActive = (() => {
            if (item.teacherAssignmentsCommTasks) {
              if (location.pathname !== ROUTES.TEACHER_ASSIGNMENTS) return false;
              return new URLSearchParams(location.search).get("open") === "communication";
            }
            if (item.teacherAssignmentsDefault) {
              if (location.pathname !== ROUTES.TEACHER_ASSIGNMENTS) return false;
              return new URLSearchParams(location.search).get("open") !== "communication";
            }
            if (item.activeRoutes) {
              return item.activeRoutes.some(
                (r) => location.pathname === r || location.pathname.startsWith(r + "/")
              );
            }
            return location.pathname === item.route;
          })();

          if (item.children) {
            return (
              <div key={item.route}>
                <button
                  type="button"
                  onClick={() => setPeopleOpen((v) => !v)}
                  className={clsx(
                    "group w-full flex items-center gap-4 px-5 py-4 rounded-2xl font-semibold text-sm transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl border-2 text-left",
                    isActive
                      ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/25 border-indigo-400 scale-[1.02]"
                      : "bg-white/70 dark:bg-white/10 backdrop-blur-sm text-slate-700 dark:text-slate-200 hover:bg-white hover:dark:bg-white/20 border-transparent hover:border-indigo-200 dark:hover:border-indigo-700 shadow-md hover:shadow-lg"
                  )}
                  style={{
                    boxShadow: isActive
                      ? "0 10px 25px -5px rgba(99, 102, 241, 0.6), 0 20px 40px -10px rgba(79, 70, 229, 0.3)"
                      : "0 4px 12px rgba(0,0,0,0.08)",
                  }}
                >
                  <div
                    className={clsx(
                      "w-10 h-10 rounded-2xl flex items-center justify-center shadow-lg transition-all duration-300 group-hover:rotate-6",
                      isActive
                        ? "bg-white/20 backdrop-blur-sm shadow-white/50"
                        : "bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/50 dark:to-purple-900/50 shadow-indigo-200/50"
                    )}
                  >
                    <Icon
                      className={clsx(
                        "w-6 h-6",
                        isActive
                          ? "text-white drop-shadow-lg"
                          : "text-indigo-600 dark:text-indigo-300"
                      )}
                    />
                  </div>
                  <span className="tracking-wide leading-tight flex-1">
                    {t(item.labelKey)}
                  </span>
                  {peopleOpen ? (
                    <ChevronDown className="w-5 h-5 shrink-0 text-current opacity-80" />
                  ) : (
                    <ChevronRight className="w-5 h-5 shrink-0 text-current opacity-80" />
                  )}
                </button>
                {peopleOpen && (
                  <div className="ml-10 pl-4 mt-1 mb-2 border-l border-slate-200 dark:border-slate-700 space-y-1">
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      const childActive =
                        location.pathname === child.route ||
                        location.pathname.startsWith(child.route + "/");
                      return (
                        <div key={child.route} className="relative">
                          <span
                            className="absolute -left-4 top-1/2 h-px w-4 bg-slate-200 dark:bg-slate-700"
                            aria-hidden
                          />
                          <Link
                            to={child.route}
                            onClick={() => {
                              if (
                                typeof window !== "undefined" &&
                                window.matchMedia("(max-width: 767px)").matches
                              ) {
                                onClose();
                              }
                            }}
                            className={clsx(
                              "relative flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors",
                              childActive
                                ? "bg-indigo-50 dark:bg-slate-800 font-semibold text-indigo-700 dark:text-indigo-200"
                                : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                            )}
                          >
                            <ChildIcon
                              className={clsx(
                                "h-[18px] w-[18px] shrink-0",
                                childActive
                                  ? "text-indigo-600 dark:text-indigo-300"
                                  : "text-slate-500 dark:text-slate-400"
                              )}
                            />
                            <span className="truncate">{t(child.labelKey)}</span>
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          return (
            <Link
              key={item.route}
              to={item.route}
              onClick={() => {
                if (typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches) {
                  onClose();
                }
              }}
              className={clsx(
                "group flex items-center gap-4 px-5 py-4 rounded-2xl font-semibold text-sm transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl border-2",
                isActive
                  ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/25 border-indigo-400 scale-[1.02]"
                  : "bg-white/70 dark:bg-white/10 backdrop-blur-sm text-slate-700 dark:text-slate-200 hover:bg-white hover:dark:bg-white/20 border-transparent hover:border-indigo-200 dark:hover:border-indigo-700 shadow-md hover:shadow-lg"
              )}
              style={{
                boxShadow: isActive
                  ? "0 10px 25px -5px rgba(99, 102, 241, 0.6), 0 20px 40px -10px rgba(79, 70, 229, 0.3)"
                  : "0 4px 12px rgba(0,0,0,0.08)",
              }}
            >
              <div
                className={clsx(
                  "w-10 h-10 rounded-2xl flex items-center justify-center shadow-lg transition-all duration-300 group-hover:rotate-6",
                  isActive
                    ? "bg-white/20 backdrop-blur-sm shadow-white/50"
                    : "bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/50 dark:to-purple-900/50 shadow-indigo-200/50"
                )}
              >
                <Icon
                  className={clsx(
                    "w-6 h-6",
                    isActive ? "text-white drop-shadow-lg" : "text-indigo-600 dark:text-indigo-300"
                  )}
                />
              </div>

              <span className="tracking-wide leading-tight">
                {LABELS[item.labelKey] ?? item.labelKey}
              </span>

              {isActive && <div className="ml-auto w-2 h-2 bg-white rounded-full shadow-lg animate-pulse" />}
            </Link>
          );
        })}
      </nav>
    </div>
  );
};